import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { useAddPhone, useUpdatePhone } from '../hooks'
import { phoneSchema, type PhoneFormValues } from '../schema'
import type { PhoneDto } from '../types'

interface PhoneFormSheetProps {
  customerId: number
  /** When provided, the sheet edits an existing phone; otherwise it creates a new one. */
  phone?: PhoneDto | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function PhoneFormSheet({ customerId, phone, open, onOpenChange }: PhoneFormSheetProps) {
  const isEdit = phone != null && phone.id != null
  const add = useAddPhone(customerId)
  const update = useUpdatePhone(customerId)

  const form = useForm<PhoneFormValues>({
    resolver: zodResolver(phoneSchema),
    defaultValues: {
      value: phone?.value ?? '',
      label: phone?.label ?? '',
      isPrimary: phone?.isPrimary ?? false,
    },
  })

  // Reset whenever the editor target changes (open/close cycle).
  useEffect(() => {
    if (open) {
      form.reset({
        value: phone?.value ?? '',
        label: phone?.label ?? '',
        isPrimary: phone?.isPrimary ?? false,
      })
    }
  }, [open, phone, form])

  const onSubmit = async (values: PhoneFormValues) => {
    try {
      if (isEdit && phone?.id != null) {
        await update.mutateAsync({
          phoneId: phone.id,
          phone: { id: phone.id, value: values.value, label: values.label || null, isPrimary: values.isPrimary, isActive: true },
        })
        toast.success('Telefon zaktualizowany')
      } else {
        await add.mutateAsync({ id: null, value: values.value, label: values.label || null, isPrimary: values.isPrimary, isActive: true })
        toast.success('Telefon dodany')
      }
      onOpenChange(false)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd'
      toast.error(`Nie udało się zapisać: ${msg}`)
    }
  }

  const submitting = add.isPending || update.isPending

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle>{isEdit ? 'Edytuj telefon' : 'Dodaj telefon'}</SheetTitle>
          <SheetDescription>Numer może być oznaczony jako główny - max jeden aktywny.</SheetDescription>
        </SheetHeader>
        <div className="px-4 pb-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-5">
              <FormField
                control={form.control}
                name="value"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Numer *</FormLabel>
                    <FormControl><Input {...field} placeholder="+48 600 100 200" /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="label"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Etykieta</FormLabel>
                    <FormControl><Input {...field} placeholder="komórka / firmowy / stacjonarny" /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="isPrimary"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center gap-2">
                    <FormControl>
                      <input
                        type="checkbox"
                        className="size-4 accent-primary"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    </FormControl>
                    <FormLabel className="!mt-0 cursor-pointer">Główny telefon klienta</FormLabel>
                  </FormItem>
                )}
              />
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
                  Anuluj
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? 'Zapisywanie...' : isEdit ? 'Zapisz' : 'Dodaj'}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </SheetContent>
    </Sheet>
  )
}

/** Convenience hook: state + handlers for opening the sheet in add or edit mode. */
export function usePhoneFormSheet(customerId: number) {
  const [open, setOpen] = useState(false)
  const [target, setTarget] = useState<PhoneDto | null>(null)
  return {
    open,
    target,
    openAdd: () => {
      setTarget(null)
      setOpen(true)
    },
    openEdit: (phone: PhoneDto) => {
      setTarget(phone)
      setOpen(true)
    },
    close: () => setOpen(false),
    setOpen,
    sheet: (
      <PhoneFormSheet
        customerId={customerId}
        phone={target}
        open={open}
        onOpenChange={setOpen}
      />
    ),
  }
}
